"use client";

import { useRef, useEffect, useCallback, forwardRef, useMemo, type CSSProperties } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import dynamic from "next/dynamic";
import { useObjectCoverAnchor } from "./_lib/objectCoverHotspot";
import { usePrefersReducedMotion } from "./_lib/usePrefersReducedMotion";
import { useIsMobile } from "./_lib/useMediaQuery";
import {
  PRIMARY_BUTTON_FILL,
  primaryButtonLabelClass,
  primaryButtonScaleLayerClass,
} from "./_lib/primaryButton";
import { BETA_ACCESS_FORM_URL } from "./_lib/betaAccessUrl";

const PhoneScene = dynamic(() => import("./PhoneScene"), { ssr: false });

gsap.registerPlugin(ScrollTrigger);

const DEG2RAD = Math.PI / 180;

const CLOSING_BG = {
  light: "/light_new.webp",
  dark: "/dark_new.webp",
} as const;

const SCROLL_LIGHT_CROSSFADE = {
  /** Crossfade begins after the section has scrolled further into view. */
  start: "top 42%",
  /** Crossfade completes when section top reaches the top of the viewport. */
  end: "top 0%",
} as const;

const SCROLL_ENTRANCE_START = "top 85%";

/**
 * Phone scrubs from above the section into its anchored position.
 * Progress 1 = section top near the viewport top (final destination).
 */
const PHONE_SCROLL_RANGE = {
  start: "top bottom",
  end: "top top",
} as const;

/** Start higher than the previous entrance, then land at yPercent 0. */
const PHONE_SCROLL_FROM_Y_PERCENT = -105;

const CLOSING_BG_FRAME_CLASS = "absolute pointer-events-none inset-0";

const CLOSING_BG_IMG_CLASS =
  "h-full w-full select-none object-cover md:object-[center_72%] max-md:object-[62%_72%]";

const CLOSING_IMAGE = { width: 3840, height: 2160 } as const;

/** Lamp-base power switch in normalized image coordinates (0-1). */
const LAMP_ANCHOR = { x: 0.883, y: 0.762 } as const;

/** Fine-tune in screen pixels after object-cover anchoring. */
const LAMP_OFFSET = { x: -50, y: -100 } as const;

/** Keep the lamp toggle at least this far from the section's right edge. */
const LAMP_MIN_RIGHT_MARGIN = 80;
/** Half of md button width (32px); conservative clamp for mobile (28px) too. */
const LAMP_BUTTON_HALF = 16;

const LAMP_TOGGLE_DURATION = 0.85;

const CLOSING_OBJECT_POSITION = {
  x: { kind: "percent", value: 50 } as const,
  y: { kind: "percent", value: 72 } as const,
} as const;

/** Pan the desk scene left on narrow viewports; y matches desktop so phone stays on the book. */
const CLOSING_OBJECT_POSITION_MOBILE = {
  x: { kind: "percent", value: 62 } as const,
  y: { kind: "percent", value: 72 } as const,
} as const;

/** Vertical nudge only; horizontal fit comes from CLOSING_OBJECT_POSITION_MOBILE. */
const MOBILE_SCENE_TRANSLATE_Y = 112;

const CLOSING_OVERLAY_BG =
  "linear-gradient(180deg, rgba(13,0,77,0.72) 0%, rgba(13,0,77,0.28) 38%, rgba(13,0,77,0.08) 62%, rgba(13,0,77,0.35) 100%)";

const CLOSING_OVERLAY_BG_MOBILE =
  "linear-gradient(180deg, rgba(13,0,77,0.55) 0%, rgba(13,0,77,0.18) 38%, rgba(13,0,77,0.04) 62%, rgba(13,0,77,0.15) 100%)";

/** Blends the closing section into the page bg and hides the desk-image top edge. */
const CLOSING_TOP_BLEND =
  "linear-gradient(180deg, #0D004D 0%, #0D004D 10%, rgba(13,0,77,0.9) 22%, rgba(13,0,77,0.65) 40%, rgba(13,0,77,0.3) 62%, transparent 100%)";

const CLOSING_TOP_BLEND_MOBILE =
  "linear-gradient(180deg, #0D004D 0%, #0D004D 20%, rgba(13,0,77,0.97) 32%, rgba(13,0,77,0.85) 48%, rgba(13,0,77,0.55) 64%, rgba(13,0,77,0.22) 80%, transparent 100%)";

/**
 * Phone-on-book anchors in normalized image coordinates (0-1).
 * Top = camera notch top, bottom = phone resting on the book spine.
 * Centerline shared so the wrapper auto-centers horizontally.
 */
const PHONE_ANCHOR_X = 0.587;
const PHONE_TOP_Y = 0.314;
const PHONE_BOTTOM_Y = 0.664;

/** Fine-tune final phone position after object-cover anchoring. */
const PHONE_OFFSET = { x: 81, y: -8 } as const;
/** Mobile scale is smaller; extra y brings the base back onto the book like desktop. */
const PHONE_OFFSET_MOBILE = { x: 81, y: 28 } as const;

/** Canvas width the shadow pixel offsets were tuned against (~1280px viewport). */
const SHADOW_REF_CANVAS_WIDTH = 420;

/** Soft cast shadow on the book, lamp top-right and cast down-left. */
const SHADOW_Y_OFFSET = -29;
/** Fine-tune shadow placement relative to the phone contact point. */
const SHADOW_BASE_OFFSET = { x: -26, y: 16 } as const;

function parseCssPx(value: string | number | undefined): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "number") return value;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function shadowOffsetScale(canvasWidth: number): number {
  return canvasWidth / SHADOW_REF_CANVAS_WIDTH;
}
const SHADOW_WIDTH_RATIO = 0.78;
const SHADOW_DEPTH_RATIO = 0.16;
const SHADOW_SKEW_X = 0;
const SHADOW_ROTATION = -7;
const SHADOW_MAX_OPACITY = 0.5;
/** Shadow grows from the phone base along the book on scroll. */
const SHADOW_LAND_OFFSET = { x: -64, y: -16 } as const;
const SHADOW_EDGE_START_OFFSET = { x: -248, y: -22 } as const;
const SHADOW_LAND_SCALE = { x: 1, y: 1 } as const;
const SHADOW_EDGE_START_SCALE = { x: 0.28, y: 0.88 } as const;
const SHADOW_CAST_BLUR_START = 22;
const SHADOW_CAST_BLUR_END = 8;
const SHADOW_CONTACT_BLUR_START = 14;
const SHADOW_CONTACT_BLUR_END = 5;
/** Shadow opacity at scroll start before lamp crossfade is applied. */
const SHADOW_LAND_OPACITY_FLOOR = 0.16;
/** Clip shadow to the blue book top surface; exclude the front spine face. */
const SHADOW_SURFACE_CLIP =
  "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
const SHADOW_CAST_CLIP =
  "polygon(6% 12%, 94% 12%, 94% calc(88% - 4px), 6% 88%)";
/** Keep a visible contact shadow even before the lamp fully crossfades in. */
const SHADOW_LIGHT_FLOOR = 0.25;

/**
 * Canvas wrapper geometry, matched to HeroSequence so the phone renders identically.
 * `2604 / 3180` is the HeroSequence canvas aspect ratio (W / H).
 * `0.79` is the fraction of canvas height the visible 3D phone occupies
 * (derived from fov 40 + camera z=55 + IPhoneModel scale 0.9).
 */
const PHONE_CANVAS_ASPECT = 2604 / 3180;
const PHONE_CANVAS_HEIGHT_RATIO = 0.79;
/** Vertical fraction within the phone canvas where the 3D model base sits. */
const PHONE_CANVAS_BASE_Y = 0.5 + PHONE_CANVAS_HEIGHT_RATIO / 2;
const PHONE_SIZE_SCALE = 0.85;
const PHONE_SIZE_SCALE_MOBILE = 0.8;

/** Static rotation for the closing scene (degrees → radians for Three.js). */
const CLOSING_PHONE_ROTATION = {
  x: -2 * DEG2RAD,
  y: -9 * DEG2RAD,
  z: -1.5 * DEG2RAD,
} as const;

const CLOSING_PHONE_START_ROTATION = {
  x: -60 * DEG2RAD,
  y: 36 * DEG2RAD,
} as const;

const LAMP_TOGGLE_BASE_CLASS =
  "group/lamp grid place-items-center cursor-pointer touch-manipulation rounded-full bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/60";

const LampToggleButton = forwardRef<
  HTMLButtonElement,
  {
    className?: string;
    style?: CSSProperties;
    ready?: boolean;
    label?: string;
    onClick: () => void;
  }
>(function LampToggleButton(
  { className, style, ready = true, label, onClick },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label ? `${label}: toggle desk lamp` : "Toggle desk lamp"}
      onClick={onClick}
      style={style}
      className={`${LAMP_TOGGLE_BASE_CLASS} ${className ?? ""}`}
      data-ready={ready ? true : undefined}
    >
      {label ? (
        <span className="pointer-events-none absolute left-1/2 top-[calc(100%+4px)] -translate-x-1/2 whitespace-nowrap text-[12px] font-medium tracking-wide text-white/75 transition-colors duration-300 group-hover/lamp:text-white/95">
          {label}
        </span>
      ) : null}
      <span className="absolute inset-0 rounded-full transition-all duration-300 bg-white/15 ring-1 ring-white/30 group-hover/lamp:bg-white/30 group-hover/lamp:ring-white/60" />
      <span
        className="absolute inset-0 rounded-full opacity-0 transition-opacity duration-500 group-hover/lamp:opacity-100 pointer-events-none"
        style={{
          boxShadow: "0 0 14px 4px rgba(255,255,255,0.45)",
        }}
      />
      <span className="relative h-[6px] w-[6px] rounded-full bg-white/85 transition-transform duration-300 group-hover/lamp:scale-110" />
    </button>
  );
});

export default function ClosingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const bgEnterRef = useRef<HTMLDivElement>(null);
  const lightBgRef = useRef<HTMLImageElement>(null);
  const darkBgRef = useRef<HTMLImageElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const vignetteRef = useRef<HTMLDivElement>(null);
  const mobileLampRef = useRef<HTMLButtonElement>(null);
  const phoneWrapperRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const shadowCastRef = useRef<HTMLSpanElement>(null);
  const shadowContactRef = useRef<HTMLSpanElement>(null);
  const shadowLandProgressRef = useRef(0);
  const phoneScrollStRef = useRef<ScrollTrigger | null>(null);
  const lightCrossfadeRef = useRef<gsap.core.Timeline | null>(null);
  const lightScrollStRef = useRef<ScrollTrigger | null>(null);
  const lampToggleTweenRef = useRef<gsap.core.Tween | null>(null);
  const applyShadowAppearanceRef = useRef<() => void>(() => {});
  const syncShadowFromScrollRef = useRef<() => void>(() => {});
  /** When set, lamp tap owns crossfade progress until the user leaves this section. */
  const lampOverrideProgressRef = useRef<number | null>(null);

  /** Static 3D phone refs (no cursor tracking, fixed pose). */
  const phoneRotationRef = useRef({ ...CLOSING_PHONE_ROTATION });
  const phoneCursorRef = useRef({ x: 0, y: 0 });
  const phoneScreenIndexRef = useRef(1);

  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();
  const objectPosition = isMobile
    ? CLOSING_OBJECT_POSITION_MOBILE
    : CLOSING_OBJECT_POSITION;
  const lampPosition = useObjectCoverAnchor(bgEnterRef, {
    imageWidth: CLOSING_IMAGE.width,
    imageHeight: CLOSING_IMAGE.height,
    anchorX: LAMP_ANCHOR.x,
    anchorY: LAMP_ANCHOR.y,
    objectPosition,
  });
  const phoneTopAnchor = useObjectCoverAnchor(bgEnterRef, {
    imageWidth: CLOSING_IMAGE.width,
    imageHeight: CLOSING_IMAGE.height,
    anchorX: PHONE_ANCHOR_X,
    anchorY: PHONE_TOP_Y,
    objectPosition,
  });
  const phoneBottomAnchor = useObjectCoverAnchor(bgEnterRef, {
    imageWidth: CLOSING_IMAGE.width,
    imageHeight: CLOSING_IMAGE.height,
    anchorX: PHONE_ANCHOR_X,
    anchorY: PHONE_BOTTOM_Y,
    objectPosition,
  });

  const phoneWrapperStyle = useMemo<CSSProperties | undefined>(() => {
    if (!phoneTopAnchor || !phoneBottomAnchor) return undefined;
    const phoneVisualHeight = phoneBottomAnchor.y - phoneTopAnchor.y;
    if (phoneVisualHeight <= 0) return undefined;
    const phoneScale = isMobile ? PHONE_SIZE_SCALE_MOBILE : PHONE_SIZE_SCALE;
    const phoneOffset = isMobile ? PHONE_OFFSET_MOBILE : PHONE_OFFSET;
    const canvasHeight =
      (phoneVisualHeight / PHONE_CANVAS_HEIGHT_RATIO) * phoneScale;
    const canvasWidth = canvasHeight * PHONE_CANVAS_ASPECT;
    const centerX = phoneTopAnchor.x + phoneOffset.x;
    const centerY =
      (phoneTopAnchor.y + phoneBottomAnchor.y) / 2 + phoneOffset.y;
    return {
      left: centerX - canvasWidth / 2,
      top: centerY - canvasHeight / 2,
      width: canvasWidth,
      height: canvasHeight,
    };
  }, [phoneTopAnchor, phoneBottomAnchor, isMobile]);

  const shadowStyle = useMemo<CSSProperties | undefined>(() => {
    if (!phoneWrapperStyle?.width || !phoneWrapperStyle?.height) return undefined;
    const canvasWidth = parseCssPx(phoneWrapperStyle.width);
    const canvasHeight = parseCssPx(phoneWrapperStyle.height);
    const wrapperLeft = parseCssPx(phoneWrapperStyle.left);
    const wrapperTop = parseCssPx(phoneWrapperStyle.top);
    if (
      !canvasWidth ||
      !canvasHeight ||
      wrapperLeft === undefined ||
      wrapperTop === undefined
    ) {
      return undefined;
    }

    const offsetScale = shadowOffsetScale(canvasWidth);
    const shadowWidth = canvasWidth * SHADOW_WIDTH_RATIO;
    const shadowHeight = canvasWidth * SHADOW_DEPTH_RATIO;
    const contactX = wrapperLeft + canvasWidth / 2;
    const contactY = wrapperTop + canvasHeight * PHONE_CANVAS_BASE_Y;
    return {
      left: contactX - shadowWidth / 2 + SHADOW_BASE_OFFSET.x * offsetScale,
      top:
        contactY +
        SHADOW_Y_OFFSET * offsetScale +
        SHADOW_BASE_OFFSET.y * offsetScale,
      width: shadowWidth,
      height: shadowHeight,
    };
  }, [phoneWrapperStyle]);

  const applyShadowAppearance = useCallback(() => {
    const shadow = shadowRef.current;
    if (!shadow) return;
    const land = shadowLandProgressRef.current;
    const light = lightCrossfadeRef.current?.progress() ?? 0;
    const lightMix = SHADOW_LIGHT_FLOOR + light * (1 - SHADOW_LIGHT_FLOOR);
    const landMix = SHADOW_LAND_OPACITY_FLOOR + land * (1 - SHADOW_LAND_OPACITY_FLOOR);
    gsap.set(shadow, { opacity: landMix * lightMix * SHADOW_MAX_OPACITY });
  }, []);

  const applyShadowTransform = useCallback((progress: number) => {
    const shadow = shadowRef.current;
    const cast = shadowCastRef.current;
    const contact = shadowContactRef.current;
    if (!shadow) return;

    const canvasWidth =
      phoneWrapperRef.current?.offsetWidth ?? SHADOW_REF_CANVAS_WIDTH;
    const offsetScale = shadowOffsetScale(canvasWidth);

    gsap.set(shadow, {
      skewX: SHADOW_SKEW_X,
      rotation: SHADOW_ROTATION,
      transformOrigin: "50% 0%",
      y: gsap.utils.interpolate(
        SHADOW_EDGE_START_OFFSET.y * offsetScale,
        SHADOW_LAND_OFFSET.y * offsetScale,
        progress
      ),
      x: gsap.utils.interpolate(
        SHADOW_EDGE_START_OFFSET.x * offsetScale,
        SHADOW_LAND_OFFSET.x * offsetScale,
        progress
      ),
      scaleX: gsap.utils.interpolate(
        SHADOW_EDGE_START_SCALE.x,
        SHADOW_LAND_SCALE.x,
        progress
      ),
      scaleY: gsap.utils.interpolate(
        SHADOW_EDGE_START_SCALE.y,
        SHADOW_LAND_SCALE.y,
        progress
      ),
    });

    const castBlur = gsap.utils.interpolate(
      SHADOW_CAST_BLUR_START,
      SHADOW_CAST_BLUR_END,
      progress
    );
    const contactBlur = gsap.utils.interpolate(
      SHADOW_CONTACT_BLUR_START,
      SHADOW_CONTACT_BLUR_END,
      progress
    );
    if (cast) {
      gsap.set(cast, { filter: `blur(${castBlur}px)` });
    }
    if (contact) {
      gsap.set(contact, { filter: `blur(${contactBlur}px)` });
    }
  }, []);

  const syncShadowFromScroll = useCallback(() => {
    const phoneSt = phoneScrollStRef.current;
    if (phoneSt) {
      shadowLandProgressRef.current = phoneSt.progress;
      applyShadowTransform(phoneSt.progress);
      applyShadowAppearance();
      return;
    }
    applyShadowAppearance();
  }, [applyShadowAppearance, applyShadowTransform]);

  useEffect(() => {
    applyShadowAppearanceRef.current = applyShadowAppearance;
    syncShadowFromScrollRef.current = syncShadowFromScroll;
  }, [applyShadowAppearance, syncShadowFromScroll]);

  const toggleLighting = useCallback(() => {
    const lightTl = lightCrossfadeRef.current;
    const lightSt = lightScrollStRef.current;
    const lightBg = lightBgRef.current;
    const darkBg = darkBgRef.current;
    if (!lightBg || !darkBg) return;

    const manualTargets = [lightBg, darkBg, overlayRef.current, vignetteRef.current].filter(
      Boolean
    ) as gsap.TweenTarget[];

    // Reduced-motion path: no scrub timeline, tween layers directly.
    if (!lightTl || !lightSt) {
      const currentlyLit = (gsap.getProperty(lightBg, "opacity") as number) > 0.5;
      gsap.killTweensOf(manualTargets);
      gsap.to(darkBg, {
        opacity: currentlyLit ? 1 : 0,
        duration: LAMP_TOGGLE_DURATION,
        ease: "power2.inOut",
      });
      gsap.to(lightBg, {
        opacity: currentlyLit ? 0 : 1,
        duration: LAMP_TOGGLE_DURATION,
        ease: "power2.inOut",
      });
      if (overlayRef.current) {
        gsap.to(overlayRef.current, {
          opacity: currentlyLit ? 1 : 0,
          duration: LAMP_TOGGLE_DURATION,
          ease: "power2.inOut",
        });
      }
      if (vignetteRef.current) {
        gsap.to(vignetteRef.current, {
          opacity: currentlyLit ? 1 : 0,
          duration: LAMP_TOGGLE_DURATION,
          ease: "power2.inOut",
        });
      }
      if (shadowRef.current) {
        gsap.to(shadowRef.current, {
          opacity: currentlyLit ? 0.125 : 0.5,
          duration: LAMP_TOGGLE_DURATION,
          ease: "power2.inOut",
        });
      }
      return;
    }

    const nextProgress = lightTl.progress() < 0.5 ? 1 : 0;
    const targetTime = nextProgress * lightTl.duration();

    // Do not kill timeline children (light/dark bg, overlays): that breaks the scrub timeline.
    lampToggleTweenRef.current?.kill();
    lightSt.disable();
    lightTl.pause();

    lampToggleTweenRef.current = lightTl.tweenTo(targetTime, {
      duration: LAMP_TOGGLE_DURATION,
      ease: "power2.inOut",
      onUpdate: applyShadowAppearance,
      onComplete: () => {
        lampToggleTweenRef.current = null;
        lampOverrideProgressRef.current = nextProgress;
        lightSt.enable();
        applyShadowAppearance();
      },
    });
  }, [applyShadowAppearance]);

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      const headline = headlineRef.current;
      const subtitle = subtitleRef.current;
      const buttons = buttonsRef.current;
      const bgEnter = bgEnterRef.current;
      const lightBg = lightBgRef.current;
      const darkBg = darkBgRef.current;
      const overlay = overlayRef.current;
      const vignette = vignetteRef.current;
      const phoneWrapper = phoneWrapperRef.current;
      const shadow = shadowRef.current;

      const textTargets = [headline, subtitle, buttons].filter(
        Boolean
      ) as HTMLElement[];

      if (prefersReducedMotion) {
        if (textTargets.length) gsap.set(textTargets, { opacity: 1, y: 0 });
        if (bgEnter) gsap.set(bgEnter, { opacity: 1, scale: 1 });
        if (lightBg) gsap.set(lightBg, { opacity: 1 });
        if (darkBg) gsap.set(darkBg, { opacity: 0 });
        if (overlay) gsap.set(overlay, { opacity: 0 });
        if (vignette) gsap.set(vignette, { opacity: 0 });
        if (phoneWrapper) {
          gsap.set(phoneWrapper, { y: 0, scale: 1, opacity: 1, transformOrigin: "50% 100%" });
        }
        Object.assign(phoneRotationRef.current, CLOSING_PHONE_ROTATION);
        if (shadow) {
          const canvasWidth =
            phoneWrapper?.offsetWidth ?? SHADOW_REF_CANVAS_WIDTH;
          const offsetScale = shadowOffsetScale(canvasWidth);
          gsap.set(shadow, {
            opacity: 0.52,
            skewX: SHADOW_SKEW_X,
            rotation: SHADOW_ROTATION,
            transformOrigin: "50% 0%",
            y: SHADOW_LAND_OFFSET.y * offsetScale,
            x: SHADOW_LAND_OFFSET.x * offsetScale,
            scaleX: SHADOW_LAND_SCALE.x,
            scaleY: SHADOW_LAND_SCALE.y,
          });
          if (shadowCastRef.current) {
            gsap.set(shadowCastRef.current, {
              filter: `blur(${SHADOW_CAST_BLUR_END}px)`,
            });
          }
          if (shadowContactRef.current) {
            gsap.set(shadowContactRef.current, {
              filter: `blur(${SHADOW_CONTACT_BLUR_END}px)`,
            });
          }
        }
        if (mobileLampRef.current) {
          gsap.set(mobileLampRef.current, { opacity: 1 });
        }
        return;
      }

      if (textTargets.length) {
        gsap.set(textTargets, { opacity: 0, y: 64 });
      }
      if (bgEnter) gsap.set(bgEnter, { opacity: 0, scale: 1.06 });
      if (lightBg) gsap.set(lightBg, { opacity: 0 });
      if (darkBg) gsap.set(darkBg, { opacity: 1 });
      if (overlay) gsap.set(overlay, { opacity: 1 });
      if (vignette) gsap.set(vignette, { opacity: 1 });
      if (phoneWrapper) {
        gsap.set(phoneWrapper, {
          yPercent: PHONE_SCROLL_FROM_Y_PERCENT,
        });
      }
      Object.assign(phoneRotationRef.current, {
        ...CLOSING_PHONE_START_ROTATION,
        z: CLOSING_PHONE_ROTATION.z,
      });
      if (shadow) {
        const canvasWidth =
          phoneWrapper?.offsetWidth ?? SHADOW_REF_CANVAS_WIDTH;
        const offsetScale = shadowOffsetScale(canvasWidth);
        gsap.set(shadow, {
          opacity: 0,
          skewX: SHADOW_SKEW_X,
          rotation: SHADOW_ROTATION,
          transformOrigin: "50% 0%",
          y: SHADOW_EDGE_START_OFFSET.y * offsetScale,
          x: SHADOW_EDGE_START_OFFSET.x * offsetScale,
          scaleX: SHADOW_EDGE_START_SCALE.x,
          scaleY: SHADOW_EDGE_START_SCALE.y,
        });
        shadowLandProgressRef.current = 0;
      }

      const bgEntranceTl = gsap.timeline({
        paused: true,
        defaults: { ease: "expo.out" },
      });
      if (bgEnter) {
        bgEntranceTl.to(bgEnter, { opacity: 1, scale: 1, duration: 1.6 });
      }

      const textEntranceTl = gsap.timeline({
        paused: true,
        defaults: { ease: "expo.out" },
      });
      if (headline) {
        textEntranceTl.to(headline, { opacity: 1, y: 0, duration: 1.1 }, 0.12);
      }
      if (subtitle) {
        textEntranceTl.to(subtitle, { opacity: 1, y: 0, duration: 0.9 }, 0.28);
      }
      if (buttons) {
        textEntranceTl.to(
          buttons,
          { opacity: 1, y: 0, duration: 0.85, ease: "power4.out" },
          0.4
        );
      }
      const playEntrance = () => {
        bgEntranceTl.play();
        textEntranceTl.play();
      };

      const lightTl = gsap.timeline({ paused: true, defaults: { ease: "none" } });
      if (darkBg) {
        lightTl.fromTo(darkBg, { opacity: 1 }, { opacity: 0, duration: 1 }, 0);
      }
      if (lightBg) {
        lightTl.fromTo(lightBg, { opacity: 0 }, { opacity: 1, duration: 1 }, 0);
      }
      if (overlay) {
        lightTl.fromTo(overlay, { opacity: 1 }, { opacity: 0, duration: 1 }, 0);
      }
      if (vignette) {
        lightTl.fromTo(vignette, { opacity: 1 }, { opacity: 0, duration: 1 }, 0);
      }

      lightCrossfadeRef.current = lightTl;

      const trigger = sectionRef.current;

      ScrollTrigger.create({
        trigger,
        start: SCROLL_ENTRANCE_START,
        onEnter: playEntrance,
        onEnterBack: playEntrance,
        onLeaveBack: () => textEntranceTl.reverse(),
      });

      if (phoneWrapper) {
        const phoneEntranceTl = gsap.timeline({
          paused: true,
          defaults: { ease: "none" },
        });
        phoneEntranceTl.fromTo(
          phoneWrapper,
          { yPercent: PHONE_SCROLL_FROM_Y_PERCENT },
          { yPercent: 0, duration: 1 },
          0
        );
        phoneEntranceTl.fromTo(
          phoneRotationRef.current,
          {
            x: CLOSING_PHONE_START_ROTATION.x,
            y: CLOSING_PHONE_START_ROTATION.y,
            z: CLOSING_PHONE_ROTATION.z,
          },
          {
            x: CLOSING_PHONE_ROTATION.x,
            y: CLOSING_PHONE_ROTATION.y,
            z: CLOSING_PHONE_ROTATION.z,
            duration: 1,
          },
          0
        );

        phoneScrollStRef.current = ScrollTrigger.create({
          trigger,
          start: PHONE_SCROLL_RANGE.start,
          end: PHONE_SCROLL_RANGE.end,
          scrub: true,
          invalidateOnRefresh: true,
          animation: phoneEntranceTl,
          onUpdate: (self) => {
            shadowLandProgressRef.current = self.progress;
            applyShadowTransform(self.progress);
            applyShadowAppearanceRef.current();
          },
          onRefresh: (self) => {
            shadowLandProgressRef.current = self.progress;
            applyShadowTransform(self.progress);
            applyShadowAppearanceRef.current();
          },
        });
        syncShadowFromScrollRef.current();
      }

      const lightSt = ScrollTrigger.create({
        trigger,
        start: SCROLL_LIGHT_CROSSFADE.start,
        end: SCROLL_LIGHT_CROSSFADE.end,
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const override = lampOverrideProgressRef.current;
          lightTl.progress(override ?? self.progress);
          applyShadowAppearanceRef.current();
        },
        onLeave: () => {
          lampOverrideProgressRef.current = null;
        },
        onLeaveBack: () => {
          lampOverrideProgressRef.current = null;
        },
      });

      lightScrollStRef.current = lightSt;
      syncShadowFromScrollRef.current();
    }, sectionRef.current);

    return () => {
      phoneScrollStRef.current = null;
      lampToggleTweenRef.current?.kill();
      lampToggleTweenRef.current = null;
      lampOverrideProgressRef.current = null;
      lightCrossfadeRef.current = null;
      lightScrollStRef.current = null;
      ctx.revert();
    };
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!shadowStyle || prefersReducedMotion) return;
    syncShadowFromScrollRef.current();
    ScrollTrigger.refresh();
  }, [shadowStyle, prefersReducedMotion]);

  const sceneLayerStyle = useMemo<CSSProperties | undefined>(
    () =>
      isMobile ? { transform: `translateY(${MOBILE_SCENE_TRANSLATE_Y}px)` } : undefined,
    [isMobile]
  );

  useEffect(() => {
    ScrollTrigger.refresh();
  }, [isMobile]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen h-[100dvh] overflow-hidden bg-[#0D004D]"
    >
      {/* Desk scene: bg, phone, shadow, and lamp toggles move together on mobile. */}
      <div
        className="absolute inset-0 z-[1]"
        style={sceneLayerStyle}
      >
        {/* Desk hero background: dark first, lights up on scroll */}
        <div ref={bgEnterRef} className="absolute inset-0 overflow-hidden">
          <div className={CLOSING_BG_FRAME_CLASS}>
            <img
              ref={lightBgRef}
              src={CLOSING_BG.light}
              alt=""
              width={3840}
              height={2160}
              className={`absolute inset-0 pointer-events-none ${CLOSING_BG_IMG_CLASS}`}
              draggable={false}
              loading="lazy"
            />
            <img
              ref={darkBgRef}
              src={CLOSING_BG.dark}
              alt=""
              width={3840}
              height={2160}
              className={`absolute inset-0 pointer-events-none ${CLOSING_BG_IMG_CLASS}`}
              draggable={false}
              loading="lazy"
            />
          </div>
        </div>

        {/* Layered phone shadow: soft cast plus contact darkening at the base. */}
        <div
          ref={shadowRef}
          aria-hidden
          className="pointer-events-none absolute z-[5] hidden md:block overflow-hidden will-change-[opacity,transform]"
          style={{
            ...shadowStyle,
            opacity: 0,
            clipPath: SHADOW_SURFACE_CLIP,
            mixBlendMode: "multiply",
          }}
        >
          <span
            ref={shadowCastRef}
            className="absolute inset-0 block"
            style={{
              clipPath: SHADOW_CAST_CLIP,
              background:
                "linear-gradient(255deg, rgba(7, 12, 32, 0.5) 0%, rgba(7, 12, 32, 0.32) 34%, rgba(7, 12, 32, 0.13) 68%, transparent 100%)",
              filter: `blur(${SHADOW_CAST_BLUR_START}px)`,
            }}
          />
          <span
            ref={shadowContactRef}
            className="absolute left-[42%] top-[-8%] block h-[30%] w-[34%]"
            style={{
              borderRadius: "999px",
              background:
                "radial-gradient(ellipse at center, rgba(4, 8, 22, 0.62) 0%, rgba(4, 8, 22, 0.34) 46%, transparent 72%)",
              filter: `blur(${SHADOW_CONTACT_BLUR_START}px)`,
            }}
          />
        </div>

        {/* 3D phone, anchored to the phone in the bg image. */}
        <div
          ref={phoneWrapperRef}
          aria-hidden
          className="pointer-events-none absolute z-[5] will-change-transform"
          style={phoneWrapperStyle}
        >
          {phoneWrapperStyle && (
            <PhoneScene
              rotationRef={phoneRotationRef}
              cursorRef={phoneCursorRef}
              screenIndexRef={phoneScreenIndexRef}
            />
          )}
        </div>

        {/* Desktop lamp toggle: above content layer so clicks register. */}
        <LampToggleButton
          onClick={toggleLighting}
          ready={Boolean(lampPosition)}
          label="Try me"
          style={
            lampPosition
              ? {
                  left: `min(${lampPosition.x + LAMP_OFFSET.x}px, calc(100% - ${LAMP_MIN_RIGHT_MARGIN + LAMP_BUTTON_HALF}px))`,
                  top: lampPosition.y + LAMP_OFFSET.y,
                  transform: "translate(-50%, -50%)",
                }
              : undefined
          }
          className="absolute z-[40] hidden md:grid h-[32px] w-[32px] opacity-0 transition-opacity duration-150 data-[ready=true]:opacity-100 pointer-events-auto"
        />

        {/* Mobile lamp toggle: anchored to the lamp base in the bg image. */}
        <LampToggleButton
          ref={mobileLampRef}
          onClick={toggleLighting}
          ready={Boolean(lampPosition)}
          style={
            lampPosition
              ? {
                  left: `min(${lampPosition.x + LAMP_OFFSET.x}px, calc(100% - ${LAMP_MIN_RIGHT_MARGIN + LAMP_BUTTON_HALF}px))`,
                  top: lampPosition.y + LAMP_OFFSET.y,
                  transform: "translate(-50%, -50%)",
                }
              : undefined
          }
          className="absolute z-[40] md:hidden h-[28px] w-[28px] opacity-0 transition-opacity duration-150 data-[ready=true]:opacity-100 pointer-events-auto"
        />
      </div>

      {/* Readability + section blend overlays: fade out when lamp is on */}
      <div
        ref={overlayRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{
          background: isMobile ? CLOSING_OVERLAY_BG_MOBILE : CLOSING_OVERLAY_BG,
        }}
      />

      {/* Top blend: smooth handoff from previous section + hide desk-image crop */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 z-[8] w-full h-[38%] md:h-[26%]"
        style={{
          background: isMobile ? CLOSING_TOP_BLEND_MOBILE : CLOSING_TOP_BLEND,
        }}
      />

      {/* Vignette: fades with lamp */}
      <div
        ref={vignetteRef}
        className="absolute inset-0 z-[4] pointer-events-none"
        style={{ boxShadow: "inset 0 0 150px 60px rgba(0, 0, 0, 0.18)" }}
      />

      {/* Content: site-width container + 500px copy column.
          The outer wrappers are `pointer-events-none` so clicks fall through
          to the lamp toggle (z-[40] on the section). The copy column
          re-enables pointer events for its own interactive children. */}
      <div className="pointer-events-none relative z-[10] h-full w-full">
        <div className="mx-auto h-full w-full max-w-[1040px] px-5 pt-[119px] md:pt-[195px]">
          <div
            ref={contentRef}
            className="pointer-events-auto flex w-full max-w-[500px] flex-col items-start gap-3"
          >
            <h1
              ref={headlineRef}
              className="w-full font-bold leading-[1.05] text-left bg-clip-text text-transparent"
              style={{
                fontSize: "clamp(36px, 4.3vw, 62px)",
                backgroundImage:
                  "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.1) 100%), linear-gradient(180deg, #fff 0%, #b7a8ea 100%)",
              }}
            >
              Built for lifelong learning for millions of students.
            </h1>
            <p
              ref={subtitleRef}
              className="w-full text-left text-white/80"
              style={{
                fontSize: "clamp(14px, 1.25vw, 18px)",
                lineHeight: "24px",
              }}
            >
              Made by Pearson, with love for education.
            </p>

            <div
              ref={buttonsRef}
              className="mt-5 flex flex-col items-start gap-4 sm:flex-row"
            >
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className={`group/top relative inline-flex items-center rounded-full cursor-pointer px-8 py-3 transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] ${primaryButtonScaleLayerClass}`}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--bx", `${e.clientX - rect.left}px`);
                  e.currentTarget.style.setProperty("--by", `${e.clientY - rect.top}px`);
                }}
              >
                <span className="relative z-[1] text-sm md:text-base font-semibold text-white">
                  Scroll to the top
                </span>
                <span className="absolute inset-0 rounded-full bg-white/10 backdrop-blur-[20px]" />
                <span
                  className="absolute inset-0 rounded-full opacity-0 group-hover/top:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    padding: "1.5px",
                    background:
                      "radial-gradient(120px circle at var(--bx, 50%) var(--by, 50%), rgba(255,255,255,0.5), rgba(255,255,255,0.15) 40%, transparent 70%)",
                    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    maskComposite: "exclude",
                    WebkitMaskComposite: "xor" as string,
                  }}
                />
                <span
                  className="absolute -inset-4 rounded-full opacity-0 group-hover/top:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(160px circle at var(--bx, 50%) var(--by, 50%), rgba(255,255,255,0.15), transparent 60%)",
                    filter: "blur(12px)",
                  }}
                />
              </button>

              <a
                href={BETA_ACCESS_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`relative inline-flex items-center justify-center rounded-full px-8 py-3 transition-transform duration-300 hover:scale-[1.03] active:scale-[0.97] ${primaryButtonScaleLayerClass}`}
              >
                <span
                  className={`relative z-[1] text-sm md:text-base font-semibold ${primaryButtonLabelClass}`}
                >
                  Get Beta Access
                </span>
                <span
                  className="absolute inset-0 rounded-full"
                  style={{ background: PRIMARY_BUTTON_FILL }}
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-0 right-0 z-[10] px-5">
        <div className="mx-auto flex max-w-[1040px] flex-col items-start gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4 text-[11px] leading-[16px] tracking-[0.02em] text-white/80">
          <p className="text-left">Developed with Cursor</p>
          <p className="text-left sm:text-right">
            <a
              href="https://sketchfab.com/3d-models/iphone-14-pro-5cb0778041a34f09b409a38c687bb1d4"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-white/40 underline-offset-2 transition-colors hover:text-white"
            >
              iPhone 14 Pro
            </a>
            {" 3D model by mister dude"}
          </p>
        </div>
      </div>
    </section>
  );
}
